import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Loader2, FileSearch, RefreshCw } from 'lucide-react';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

const ACTION_LABELS = {
  'station.create': 'Estación · creada',
  'station.update': 'Estación · actualizada',
  'station.delete': 'Estación · eliminada',
  'station.schedule_update': 'Estación · horario',
  'station_push.confirm': 'Monolito · confirmado',
  'tracking_request.approved': 'Tracking · aprobado',
  'tracking_request.rejected': 'Tracking · rechazado',
  'user.create': 'Usuario · creado',
  'user.update': 'Usuario · actualizado',
  'user.delete': 'Usuario · eliminado',
  'user.pwa_installed': 'Usuario · instaló la app',
  'user.pwa_standalone_first_open': 'Usuario · abrió app instalada',
  'parameters.update': 'Parámetros · actualizados',
  'system.factory_reset': 'Sistema · reinicio total',
};

const ACTION_COLOR = {
  'station.delete': 'bg-[#fee2e2] text-[#991b1b]',
  'user.delete': 'bg-[#fee2e2] text-[#991b1b]',
  'tracking_request.rejected': 'bg-[#fee2e2] text-[#991b1b]',
  'tracking_request.approved': 'bg-[#dcfce7] text-[#166534]',
  'station_push.confirm': 'bg-[#dcfce7] text-[#166534]',
  'station.create': 'bg-[#dbeafe] text-[#1e40af]',
  'user.create': 'bg-[#dbeafe] text-[#1e40af]',
  'user.pwa_installed': 'bg-[#dbeafe] text-[#1e40af]',
  'user.pwa_standalone_first_open': 'bg-[#e0e7ff] text-[#3730a3]',
  'parameters.update': 'bg-[#fef3c7] text-[#92400e]',
  'system.factory_reset': 'bg-[#fee2e2] text-[#991b1b]',
};

const ROLE_LABEL = { admin: 'Admin', territory_manager: 'TM', capturer: 'Tracker', agent: 'Agente' };

const fmtAt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'medium' }) : '';

const AdminAuditLog = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ actions: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [targetType, setTargetType] = useState('');
  const [fromDate, setFromDate] = useState(daysAgoIso(7));
  const [toDate, setToDate] = useState(todayIso());
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (action) params.action = action;
      if (actorId) params.actor_id = actorId;
      if (targetType) params.target_type = targetType;
      if (fromDate) params.from = new Date(fromDate + 'T00:00:00').toISOString();
      if (toDate) params.to = new Date(toDate + 'T23:59:59').toISOString();
      const r = await api.get('/admin/audit-log', { params });
      setRows(r.data.rows || []);
      setMeta(r.data.meta || { actions: [], actors: [] });
    } finally { setLoading(false); }
  }, [action, actorId, targetType, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-audit-page">
      <div>
        <h2 className="font-serif text-[34px] leading-tight tracking-tight">Auditoría</h2>
        <p className="text-[12.5px] text-[#5a5a5a] mt-1">Registro inmutable de aprobaciones y cambios sensibles del sistema.</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="audit-filters">
        <div className="lg:col-span-2">
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Acción</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 h-9 w-full text-[12.5px] border border-[#e8e5de] rounded-md px-2 bg-white" data-testid="audit-filter-action">
            <option value="">Todas</option>
            {meta.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Usuario</label>
          <select value={actorId} onChange={(e) => setActorId(e.target.value)} className="mt-1 h-9 w-full text-[12.5px] border border-[#e8e5de] rounded-md px-2 bg-white" data-testid="audit-filter-actor">
            <option value="">Todos</option>
            {meta.actors.map(a => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Desde</label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 h-9 text-[12.5px]" data-testid="audit-filter-from"/>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">Hasta</label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 h-9 text-[12.5px]" data-testid="audit-filter-to"/>
        </div>
        <div className="sm:col-span-2 lg:col-span-6 flex items-center justify-end">
          <button
            onClick={load}
            className="h-8 px-3 rounded-full bg-[#1c1c1c] text-white text-[11.5px] inline-flex items-center gap-1.5"
            data-testid="audit-refresh"
          >
            <RefreshCw size={11}/> Refrescar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[180px_180px_1fr_1fr_30px] gap-3 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
          <div>Fecha · hora</div>
          <div>Usuario</div>
          <div>Acción</div>
          <div>Objeto</div>
          <div/>
        </div>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-[12.5px] text-[#8a8579]">
            <FileSearch size={26} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
            Sin eventos para estos filtros.
          </div>
        ) : (
          <div className="divide-y divide-[#f0ede5]">
            {rows.map(r => {
              const isOpen = !!expanded[r.id];
              const hasPayload = r.payload && Object.keys(r.payload).length > 0;
              const lblCls = ACTION_COLOR[r.action] || 'bg-[#f3f0e9] text-[#3a3a3a]';
              return (
                <div key={r.id} data-testid={`audit-row-${r.id}`}>
                  <button
                    onClick={() => hasPayload && setExpanded({ ...expanded, [r.id]: !isOpen })}
                    className={`w-full grid grid-cols-[180px_180px_1fr_1fr_30px] gap-3 items-center px-4 py-2.5 text-left text-[12.5px] ${hasPayload ? 'hover:bg-[#fafaf7] cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="text-[#3a3a3a] font-mono tabular-nums text-[11.5px]">{fmtAt(r.at)}</div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[12px]">{r.actor_name || '(sistema)'}</p>
                      <p className="text-[10.5px] text-[#8a8579]">{ROLE_LABEL[r.actor_role] || r.actor_role || '—'}</p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center text-[10.5px] px-2 py-0.5 rounded-full ${lblCls}`}>
                        {ACTION_LABELS[r.action] || r.action}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate">{r.target_label || r.target_id || r.target_type || '—'}</p>
                      <p className="text-[10.5px] text-[#8a8579] truncate">{r.target_type}</p>
                    </div>
                    <div className="text-right text-[#a8a397] text-[12px]">{hasPayload ? (isOpen ? '−' : '+') : ''}</div>
                  </button>
                  {isOpen && hasPayload && (
                    <pre className="px-4 pb-3 -mt-1 bg-[#fafaf7] text-[11px] text-[#3a3a3a] overflow-x-auto whitespace-pre-wrap break-all font-mono leading-snug" data-testid={`audit-payload-${r.id}`}>
{JSON.stringify(r.payload, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLog;
